import React, { useCallback } from "react";
import { Box, Checkbox, IconButton, TextField, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutocompleteWithPagination from "../../../components/autocomplete-with-pagination";
import { productService } from "../../../utils/services/productService";
import { tableFieldSx, tableAutocompleteSx } from "../constants";
import VariantsCell from "./VariantsCell";

const ProductTableRow = ({
  entry,
  entryIndex,
  onProductChange,
  onVariantChange,
  onVariantsChange,
  onRemoveProduct,
  onPageChange,
  selectedClient,
  excludeIds,
  isSelected,
  onToggleProductSelect,
  colWidths,
  isLastRow,
  hidePageColumn = false,
}) => {
  const handlePageChange = useCallback(
    (e) => onPageChange(entryIndex, e.target.value),
    [onPageChange, entryIndex],
  );
  const handleProductSelect = useCallback(
    (prod) => onProductChange(entryIndex, prod),
    [onProductChange, entryIndex],
  );
  const handleRemove = useCallback(
    () => onRemoveProduct(entryIndex),
    [onRemoveProduct, entryIndex],
  );
  const handleToggle = useCallback(
    () => entry.product && onToggleProductSelect(entry.product._id),
    [onToggleProductSelect, entry.product],
  );
  const handleVariantFieldChange = useCallback(
    (vi, field, val) => onVariantChange(entryIndex, vi, field, val),
    [onVariantChange, entryIndex],
  );
  const handleVariantsReplace = useCallback(
    (newVariants) => onVariantsChange(entryIndex, newVariants),
    [onVariantsChange, entryIndex],
  );

  const cellSx = {
    display: "flex",
    alignItems: "flex-start",
    borderRight: "1px solid var(--border-color)",
    boxSizing: "border-box",
    overflow: "hidden",
    padding: "8px 4px",
    minHeight: "50px",
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "50px",
        borderBottom: "1px solid var(--border-color)",
        backgroundColor: isSelected ? "var(--bg-highlight)" : undefined,
      }}
    >
      {/* Checkbox */}
      <Box
        sx={{
          ...cellSx,
          width: colWidths.checkboxW,
          minWidth: colWidths.checkboxW,
          justifyContent: "flex-start",
        }}
      >
        {entry.product && (
          <Checkbox size="small" checked={isSelected} onChange={handleToggle} />
        )}
      </Box>

      {/* Page */}
      {!hidePageColumn && (
        <Box
          sx={{ ...cellSx, width: colWidths.pageW, minWidth: colWidths.pageW }}
        >
          <TextField
            size="small"
            fullWidth
            type="number"
            value={entry.page ?? ""}
            onChange={handlePageChange}
            sx={tableFieldSx}
            inputProps={{ min: 1, style: { fontSize: 13 } }}
            placeholder="Page"
          />
        </Box>
      )}

      {/* Product */}
      <Box
        sx={{
          ...cellSx,
          width: colWidths.productW,
          minWidth: colWidths.productW,
        }}
      >
        <AutocompleteWithPagination
          disabled={!selectedClient?._id}
          excludeIds={excludeIds}
          selectedItem={entry.product}
          onDataChange={handleProductSelect}
          selectedClient={selectedClient}
          width="100%"
          optionKey="name"
          hideLabel
          compact
          backgroundColor="transparent"
          apiEndPoint={(limit, skip, search, client, supplier, excludeIds) =>
            productService.fetchProductsPost({
              ...(client && { client_id: client }),
              ...(excludeIds.length > 0 && { _id: { $nin: excludeIds } }),
              ...(search && { name: { $search: search } }),
              ...(supplier && { vendor: supplier }),
              $limit: limit,
              $skip: skip,
            })
          }
          inputSx={tableAutocompleteSx}
          containerSx={{ width: "100%" }}
        />
      </Box>

      {/* SKU / Price */}
      <Box
        sx={{
          ...cellSx,
          width: colWidths.skuW,
          minWidth: colWidths.skuW,
          cursor: "pointer",
        }}
      >
        <VariantsCell
          inTable
          variants={entry.variants}
          productSkus={entry.productSkus || []}
          onVariantChange={handleVariantFieldChange}
          onVariantsChange={handleVariantsReplace}
        />
      </Box>

      {/* Delete */}
      <Box
        sx={{
          ...cellSx,
          width: colWidths.deleteW,
          minWidth: colWidths.deleteW,
          borderRight: "none",
          justifyContent: "center",
        }}
      >
        {(entry.product || !isLastRow) && (
          <Tooltip title="Remove">
            <IconButton
              size="small"
              onClick={handleRemove}
              sx={{ color: "error.main", p: 0.5 }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(ProductTableRow);
